import React, { useState } from 'react'
import clsx from 'clsx'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { secondaryButtonIconColour } from 'styles/theme/colours'
import { Box } from '@node-space/storybook-components/dist/Box'
import { Icon } from '@node-space/storybook-components/dist/Icon'
import { Text } from '@node-space/storybook-components/dist/Text'
import TextList from '@node-space/storybook-components/dist/TextList'
import { Tooltip } from '@node-space/storybook-components/dist/Tooltip'
import { logError } from '@node-space/utils'
import { Description } from 'components/text/Description'
import { DocumentUpload } from 'types/verification'

interface TooltipContent {
  title: string
  description?: string
  bulletList?: string[]
}

interface UploadFileProps {
  title: string
  file: DocumentUpload
  tooltipContent?: TooltipContent
  showIsRequired?: boolean
  setFile: (fileData: DocumentUpload) => void
  name: string
}

export default function UploadFile({
  title,
  file,
  tooltipContent,
  showIsRequired,
  setFile,
  name,
}: UploadFileProps) {
  const { t } = useTranslation()
  const [error, setError] = useState<string | undefined>()

  const onDrop = acceptedFiles => {
    // -- clear state
    setError(null)
    setFile(null)
    try {
      // -- check if file exceeds max size
      const acceptedFile = acceptedFiles[0]

      if (!acceptedFile) {
        setError(t('uploadDocument.uploadPDFOnly'))
        return
      }

      if (acceptedFile?.size > 5000000) {
        setError(t('uploadDocument.smallerFileRequired'))
        return
      }

      // -- read file data and set state
      const reader = new FileReader()
      // -- to handle other events on the reader, use 'reader.onabort' and 'reader.onerror'
      reader.onload = () => {
        const data = reader?.result
        const fileData = { fileData: acceptedFile, buffer: data }
        setFile && setFile(fileData)
      }
      reader.readAsDataURL(acceptedFile)
    } catch (error) {
      logError('Error from UploadFile - onDrop method', error)
      setError(t('oopsSomethingWentWrong'))
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
  })

  const outerClassName = clsx(
    'flex flex-col h-small w-full border-2 border-solid rounded-lg items-center py-5 cursor-pointer justify-center content-center',
    {
      'border-green-400 bg-green-50 border-solid': file,
      'border-dashed': !file && !error,
      'bg-gray-25': isDragActive,
      'border-red-400 bg-red-50 border-solid': error || showIsRequired,
    }
  )

  return (
    <Box>
      <Box flex paddingB={4} gapX={4} flexItemsCenter>
        <Text size="sm" weight="medium" color="black">
          {title}
        </Text>

        {!!tooltipContent && (
          <Tooltip
            id="merchant-tooltip"
            position="right"
            hasMaxWidth
            bodyContent={
              <>
                <Text size="sm" color="inherit">
                  {tooltipContent?.title}
                </Text>
                {tooltipContent?.bulletList && (
                  <TextList
                    textSize="sm"
                    textColor="inherit"
                    listStyle="bullet"
                    listItems={tooltipContent?.bulletList?.map((item, index) => ({
                      key: `list-item-${index}`,
                      children: item,
                    }))}
                    className="pl-3"
                  />
                )}
              </>
            }
          >
            <Icon name="InfoCircleIcon" color="primary-500" />
          </Tooltip>
        )}
      </Box>
      <div className={outerClassName} {...getRootProps()}>
        <input data-testid="document-upload-zone" name={name} {...getInputProps()} />

        {isDragActive ? (
          <Text size="sm" color="grey" weight="semibold">
            {t('uploadDocument.dropFile')}
          </Text>
        ) : file ? (
          <Box flex direction="col" gapY={4} flexItemsCenter>
            <Text size="sm" color="grey" textStyle="underline">
              {file?.fileData?.name}
            </Text>
            <Description color="primary-500">{t('uploadDocument.replaceFile')}</Description>
          </Box>
        ) : (
          <Box flex direction="col" gapY={4} flexItemsCenter>
            <Box flex gapX={4}>
              <Icon name="UploadIcon" color={secondaryButtonIconColour} />
              <Description color="primary-500">{t('uploadDocument.uploadFile')}</Description>
            </Box>
            <Description>{error ? error : t('uploadDocument.uploadAPDF')}</Description>
          </Box>
        )}
      </div>
      {showIsRequired && (
        <Box paddingT={4}>
          <Description color="error">{t('uploadDocument.fileRequired')}</Description>
        </Box>
      )}
    </Box>
  )
}
